import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import DynamicTable from '../../../Dynamic-table';

export interface LinkedCrossMatchReport {
    crossMatchId: string;
    slipNo?: number | string;
    bagNo?: number | string;
    bloodGroup?: string;
    component?: string;
    crossMatchResult?: string;
    crossMatchDate?: string;
    performedBy?: string;
    linkedAt: string;
    linkedBy?: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    mrn?: number | string;
    /** Currently linked report for this transfusion row (0 or 1). */
    linkedReports: LinkedCrossMatchReport[];
    /** Cross match ids already linked on other transfusion rows. */
    usedByOtherIds?: string[];
    onLink: (crossMatch: any) => void;
    onUnlink: (crossMatchId: string) => void;
    /** Close dialog after a successful link (per-row flow). */
    singleSelect?: boolean;
}

const LOOKBACK_MONTHS = 12;
const MAX_RESULTS = 20;

export const fetchPatientCrossMatches = async (params: Record<string, any>) => {
    const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/cross-match/findAllCrossMatches`,
        {
            params: { count: MAX_RESULTS, pageNo: 1, ...params },
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
    );
    return response?.data?.data || [];
};

export const fetchCrossMatchById = async (id: string) => {
    const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/cross-match/getOneCrossMatchById/${id}`,
        {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
    );
    return response?.data?.data || null;
};

const formatBloodGroup = (item: any) => {
    const group = item?.slipData?.bloodGroup || item?.patientDetails?.bloodGroup || '';
    const rh = item?.slipData?.rhFactor || '';
    return `${group}${rh}`.trim() || '-';
};

const LinkCrossMatchDialog: React.FC<Props> = ({
    open,
    onClose,
    mrn,
    linkedReports,
    usedByOtherIds = [],
    onLink,
    onUnlink,
    singleSelect = false,
}) => {
    const [crossMatches, setCrossMatches] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (!mrn) {
            toast.error('Patient MRN not found.');
            return;
        }
        const load = async () => {
            setLoading(true);
            try {
                const rows = await fetchPatientCrossMatches({
                    fromDate: dayjs().subtract(LOOKBACK_MONTHS, 'month').format('YYYY-MM-DD'),
                    toDate: dayjs().format('YYYY-MM-DD'),
                    search: String(mrn),
                });
                setCrossMatches(
                    (rows || []).filter(
                        (row: any) =>
                            String(row?.patientDetails?.mrn ?? '') === String(mrn) ||
                            String(row?.slipData?.mrn ?? '') === String(mrn)
                    )
                );
            } catch (error: any) {
                toast.error(error?.response?.data?.message || 'Failed to load cross match reports.');
                setCrossMatches([]);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [open, mrn]);

    const isLinkedHere = (id: string) => linkedReports.some(r => r.crossMatchId === id);
    const isUsedElsewhere = (id: string) => usedByOtherIds.includes(id);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>
                Link Cross Match Report
                <Typography sx={{ fontSize: '12px', color: 'gray' }}>
                    Latest cross matches of MRN {mrn} (last {LOOKBACK_MONTHS} months) — optional
                </Typography>
            </DialogTitle>
            <DialogContent>
                <DynamicTable
                    height="55vh"
                    loading={loading}
                    headers={['Slip No', 'Bag No', 'Blood Group', 'Component', 'Result', 'Date', 'Action']}
                    data={crossMatches.map((item: any) => [
                        item?.slipNo,
                        item?.bagNo,
                        formatBloodGroup(item),
                        item?.bagData?.componentName || item?.componentName || '-',
                        item?.crossMatchResult || '-',
                        item?.crossMatchDate
                            ? dayjs(item.crossMatchDate).format('DD.MM.YYYY hh:mm A')
                            : dayjs(item?.createdAt).format('DD.MM.YYYY hh:mm A'),
                    ])}
                    actionButtons={[
                        {
                            icon: (rowIndex: number) => {
                                const id = crossMatches[rowIndex]?._id;
                                if (isLinkedHere(id)) {
                                    return (
                                        <Tooltip title="Unlink">
                                            <LinkOffIcon sx={{ color: '#FF5454' }} />
                                        </Tooltip>
                                    );
                                }
                                if (isUsedElsewhere(id)) {
                                    return (
                                        <Tooltip title="Already linked to another transfusion">
                                            <LinkIcon sx={{ color: '#BDBDBD' }} />
                                        </Tooltip>
                                    );
                                }
                                return (
                                    <Tooltip title="Link">
                                        <LinkIcon sx={{ color: '#1976D2' }} />
                                    </Tooltip>
                                );
                            },
                            disabled: (rowIndex: number) =>
                                isUsedElsewhere(crossMatches[rowIndex]?._id) &&
                                !isLinkedHere(crossMatches[rowIndex]?._id),
                            onClick: (rowIndex: number) => {
                                const item = crossMatches[rowIndex];
                                if (isUsedElsewhere(item?._id) && !isLinkedHere(item?._id)) return;
                                if (isLinkedHere(item?._id)) {
                                    onUnlink(item._id);
                                } else {
                                    onLink(item);
                                    if (singleSelect) onClose();
                                }
                            },
                        },
                    ]}
                />
                {!loading && crossMatches.length === 0 && (
                    <Typography sx={{ fontSize: '12px', textAlign: 'center', mt: 2, color: 'gray' }}>
                        No cross match reports found for this patient.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">Done</Button>
            </DialogActions>
        </Dialog>
    );
};

export default LinkCrossMatchDialog;
